import React, { useState } from "react";
import UploadClientTemplateModal from "./UploadClientTemplateModal";
import { API_BASE, apiFetch } from "../../api/http";

interface CustomDataLayerUploadProps {
  /**
   * Optional extra class names to append to the outer container
   */
  className?: string;

  /**
   * Called after the user has selected a file.
   * You can handle the file upload in the parent by passing this callback.
   */
  onFileSelected?: (file: File) => void;
}

/**
 * Renders a button that lets the user choose a JSON or CSV file representing
 * a custom data layer. The actual upload logic can be provided by the parent
 * component via the `onFileSelected` callback.
 */
const CustomDataLayerUpload: React.FC<CustomDataLayerUploadProps> = ({ className = "", onFileSelected }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleUpload = async (data: {
    file: File;
    documentType: "protocol" | "protective" | "clause";
    clientName: string;
    projectNumber: string;
    autoApply: boolean;
  }) => {
    try {
      const formData = new FormData();
      formData.append('clientName', data.clientName);
      formData.append('projectNumber', data.projectNumber);
      formData.append('documentType', data.documentType);
      formData.append('file', data.file);
      const response = await apiFetch(`${API_BASE}/api/clientTemplates`, {
        method: 'POST',
        body: formData,
      });
      // F2: Parse JSON once and check response status
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || response.statusText);
      }
      console.log('Uploaded template result:', result);
      onFileSelected?.(data.file);
    } catch (error) {
      console.error('Upload failed:', error);
      // F6: Safe error message extraction
      const message = error instanceof Error ? error.message : String(error);
      alert('Upload failed: ' + message);
    }
  };

  return (
    <>
      <button
        onClick={handleOpenModal}
        className={[
          // keep it inline so it only takes as much room as it needs
          "inline-flex",
          // prevent the label from ever wrapping
          "whitespace-nowrap",
          // center contents
          "items-center justify-center",
          // slightly smaller text & padding so it fits
          "text-sm px-3 py-2",
          // colors & rounding
          "bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-md",
          // allow any extra classes passed in (e.g. mr-4 on some screens)
          className
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Upload Preferred Language Template
      </button >

      <UploadClientTemplateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpload={handleUpload}
      />
    </>
  );
};

export default CustomDataLayerUpload; 